import {
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { concat } from 'lodash';
import {
  FetchReturn,
  IProductVariationFlavor,
  PaginationResponse,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetProductVariationFlavors = (
  filters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IProductVariationFlavor[]>,
) => {
  return useQuery({
    queryKey: queryKeys.productVariationFlavors.all(filters),
    queryFn: () => fetchProductVariationFlavors(filters),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

export const useGetProductVariationFlavorsWithPagination = (
  filters: string[] = [],
  overrideQuerySetting?: UseInfiniteQueryOptions<
    PaginationResponse<IProductVariationFlavor[]>
  >,
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.productVariationFlavors.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchProductVariationFlavors(
        concat(filters, [`page=${pageParam}`]),
        true,
      ),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    ...overrideQuerySetting,
  });
};

const fetchProductVariationFlavors = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IProductVariationFlavor[]>> => {
  const hasPageSize =
    isPagination || filters.some((f) => /^pageSize=\d+$/i.test(f));

  const finalFilters = hasPageSize ? filters : [...filters, 'pageSize=0'];

  const url = `/productVariationFlavors${generateQuery([], finalFilters)}`;
  const response = await requestCCApi({ url });

  if (!isPagination) {
    return response.data as FetchReturn<T, IProductVariationFlavor[]>;
  } else {
    return {
      data: response.data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IProductVariationFlavor[]>;
  }
};
